import { format, parseISO } from "date-fns";
import type {
  InvoiceOrderDto,
  InvoiceOrderSessionDto,
  InvoiceOrderStatusChangeDto,
  SeatOrderPaymentMethod,
  SeatOrderStatus,
} from "@/types/api";
import { formatSessionDate, formatSessionTimes } from "@/features/instructor-led/iltSessions";
import { seatOrderStatusLabel } from "@/features/seat-orders/seatOrders";
import { escapeCsvField } from "@/utils/csv";
import { toNum } from "@/utils/course-formatters";

/** The statuses the Orders list can be filtered to (LMS-151), in the order an order moves through them. */
export const INVOICE_ORDER_STATUSES: SeatOrderStatus[] = [
  "AwaitingInvoice",
  "Invoiced",
  "Paid",
  "Cancelled",
];

/** Paid reads as done, Cancelled as over, and the rest as still to do. */
export const invoiceOrderStatusColor = (status: SeatOrderStatus) =>
  status === "Paid" ? "success" : status === "Cancelled" ? "gray" : "warning";

/** The payment methods the Orders list can be filtered to (LMS-193). */
export const ORDER_PAYMENT_METHODS: SeatOrderPaymentMethod[] = ["Card", "Invoice"];

export const ORDER_PAYMENT_METHOD_LABELS: Partial<Record<SeatOrderPaymentMethod, string>> = {
  Card: "Card",
  Invoice: "Invoice",
};

/** A card order (LMS-193): its status is its card payment's, and it isn't moved on here. */
export const isCardOrder = (order: Pick<InvoiceOrderDto, "paymentMethod">) =>
  order.paymentMethod === "Card";

/** Where an order stands: an invoice order's status, or a card order's payment status, Paid or Refunded (LMS-193). */
export const orderStatusLabel = (
  order: Pick<InvoiceOrderDto, "paymentMethod" | "status" | "cardPaymentStatus">,
) =>
  isCardOrder(order)
    ? order.cardPaymentStatus === "Refunded"
      ? "Refunded"
      : "Paid"
    : seatOrderStatusLabel(order.status);

/** Paid reads as done and Refunded as over, as for an invoice order. */
export const orderStatusColor = (
  order: Pick<InvoiceOrderDto, "paymentMethod" | "status" | "cardPaymentStatus">,
) =>
  isCardOrder(order)
    ? order.cardPaymentStatus === "Refunded"
      ? "gray"
      : "success"
    : invoiceOrderStatusColor(order.status);

/** One line of an order's history: what happened, e.g. "Marked invoiced (INV-001)" or "2 seats cancelled". */
export function statusChangeLabel(change: InvoiceOrderStatusChangeDto) {
  if (!change.fromStatus) return "Order placed, awaiting invoice";
  // Some of the order's seats cancelled, its status unchanged (LMS-195).
  const seatsCancelled = Number(change.seatsCancelled ?? 0);
  if (seatsCancelled > 0)
    return seatsCancelled === 1 ? "1 seat cancelled" : `${seatsCancelled} seats cancelled`;
  switch (change.toStatus) {
    case "Invoiced":
      return change.invoiceReference
        ? `Marked invoiced (${change.invoiceReference})`
        : "Marked invoiced";
    case "Paid":
      return "Marked paid";
    case "Cancelled":
      return "Cancelled";
    default:
      return change.toStatus;
  }
}

/** When something happened, e.g. 4 Nov 2026, 17:05. */
export const formatOrderTime = (value: string) => format(new Date(value), "d MMM yyyy, HH:mm");

/** A session as the Orders list shows it, e.g. Tue 1 Dec 2026, 09:30 to 16:30, Pearce Institute. */
export const formatOrderSession = (session: InvoiceOrderSessionDto) =>
  `${formatSessionDate(session)}, ${formatSessionTimes(session)}, ${session.venueName}`;

/** Reads the number of uninvited seats an admin types in to cancel (LMS-195): a whole number from 0 to `max`, otherwise null. */
export function parseUninvitedSeats(value: string, max: number): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return count <= max ? count : null;
}

/** The UK day an invoice is due (LMS-194), e.g. 5 Dec 2026, from the API's yyyy-MM-dd. */
export const formatDueDate = (value: string) => format(parseISO(value), "d MMM yyyy");

/** An amount in pence as plain pounds for a spreadsheet, e.g. 1234.50. */
const csvAmount = (pence: number | string) => (toNum(pence) / 100).toFixed(2);

/** The columns of the invoice orders export (LMS-194), in order. */
export const INVOICE_ORDERS_CSV_COLUMNS = [
  "Order ID",
  "Placed",
  "Billing name",
  "Billing email",
  "Billing address",
  "PO number",
  "VAT number",
  "Course",
  "Session",
  "Venue",
  "Seats",
  "Seat price",
  "Amount",
  "Currency",
] as const;

/**
 * The invoice orders as a CSV file (LMS-194), one row per order, with everything needed to raise each invoice in the
 * accounting system: billing details, PO and VAT numbers, course, session, number of seats and amount. Amounts are
 * plain pounds (e.g. 1234.50) so a spreadsheet reads them as numbers; an online course's order has no session or venue.
 */
export function invoiceOrdersCsv(orders: InvoiceOrderDto[]): string {
  const text = (value: string | null | undefined) => escapeCsvField(value ?? "");
  const header = INVOICE_ORDERS_CSV_COLUMNS.map(text).join(",");
  const rows = orders.map((order) =>
    [
      text(order.seatOrderId),
      text(formatOrderTime(order.placedAt)),
      text(order.billingName),
      text(order.billingEmail),
      text(order.billingAddress),
      text(order.poNumber),
      text(order.vatNumber),
      text(order.courseTitle),
      text(
        order.session
          ? `${formatSessionDate(order.session)}, ${formatSessionTimes(order.session)}`
          : "Online course",
      ),
      text(order.session ? `${order.session.venueName}, ${order.session.venueAddress}` : ""),
      toNum(order.seatCount),
      csvAmount(order.seatPricePence),
      csvAmount(order.amountPence),
      text(order.currency),
    ].join(","),
  );
  return [header, ...rows].join("\n");
}
